import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/ui/toast/toast.service';
import { hasAuthorizationError } from '@shared/ui/toast/toast-message.utils';
import { ConfirmActionService } from '@shared/ui/confirm-action-dialog/confirm-action.service';
import { ProductFormPostCreateUploadResult } from '@features/catalog/services/product-form-save-coordinator.service';
import { ProductGalleryImage } from '@features/catalog/models/product.models';
import { mediaUrlForContext } from '@core/media/utils/media-image.util';
import { applyCatalogMutationErrors } from '@features/catalog/presentation/utils/catalog-mutation-errors';
import {
  AttributeGroupNameConflict,
  buildAttributeGroupNameConflictToast,
} from '@features/catalog/utils/product-attribute-groups.validation';
import {
  ModifierGroupNameConflict,
  buildModifierGroupNameConflictToast,
} from '@features/catalog/utils/product-modifier-groups-name.validation';

export interface HandleCatalogMutationErrorsOptions {
  form: FormGroup;
  /** Productos variables: no dejar `price.min` residual en el control oculto. */
  skipProductPriceMinMapping?: boolean;
  onVariantCombinationConflict?: () => void;
  onLockVersionConflict?: () => void;
  onVariantPricesInvalid?: () => void;
  onVariableNeedsVariants?: () => void;
  onVariantAttributesIncomplete?: () => void;
  onAttributeGroupNameConflict?: () => void;
  onAttributeValueConflict?: () => void;
  onModifierGroupNameConflict?: () => void;
  /** Conflicto de nombre inline ya detectado en UI (toast más preciso). */
  primaryAttributeGroupNameConflict?: AttributeGroupNameConflict | null;
  primaryModifierGroupNameConflict?: ModifierGroupNameConflict | null;
  /** Nombres de variaciones sin precio en el estado del formulario (para el toast). */
  invalidVariantPriceNames?: string;
}

export interface PostCreateUploadPatches {
  pendingProductImageFile: null;
  isUploadingImage: false;
  imagePreviewUrl: string | null;
  imageUrl: string;
  pendingGalleryFilesCleared: boolean;
  isUploadingGallery: false;
  galleryItemsToAppend: ProductGalleryImage[];
}

export interface CatalogMutationFeedbackResult {
  handled: boolean;
  lockConflict: boolean;
}

@Injectable()
export class ProductFormMutationFeedbackService {
  private readonly confirmAction = inject(ConfirmActionService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  handleCatalogMutationErrors(
    errors: readonly string[],
    options: HandleCatalogMutationErrorsOptions
  ): CatalogMutationFeedbackResult {
    if (!errors.length) {
      return { handled: false, lockConflict: false };
    }

    let lockConflict = false;
    let variantPricesInvalid = false;
    let variableNeedsVariants = false;
    let variantAttributesIncomplete = false;
    let attributeGroupNameConflict = false;
    let attributeValueConflict = false;
    let modifierGroupNameConflict = false;
    let variantCombinationConflict = false;
    const unmapped = applyCatalogMutationErrors(errors, {
      form: options.form,
      skipProductPriceMinMapping: options.skipProductPriceMinMapping,
      onVariantCombinationConflict: () => {
        variantCombinationConflict = true;
        options.onVariantCombinationConflict?.();
      },
      onVariantPricesInvalid: () => {
        variantPricesInvalid = true;
        options.onVariantPricesInvalid?.();
      },
      onVariableNeedsVariants: () => {
        variableNeedsVariants = true;
        options.onVariableNeedsVariants?.();
      },
      onVariantAttributesIncomplete: () => {
        variantAttributesIncomplete = true;
        options.onVariantAttributesIncomplete?.();
      },
      onAttributeGroupNameConflict: () => {
        attributeGroupNameConflict = true;
        options.onAttributeGroupNameConflict?.();
      },
      onAttributeValueConflict: () => {
        attributeValueConflict = true;
        options.onAttributeValueConflict?.();
      },
      onModifierGroupNameConflict: () => {
        modifierGroupNameConflict = true;
        options.onModifierGroupNameConflict?.();
      },
      onLockVersionConflict: () => {
        lockConflict = true;
        options.onLockVersionConflict?.();
      },
    });

    if (lockConflict) {
      return { handled: true, lockConflict: true };
    }

    if (variantPricesInvalid) {
      const names =
        options.invalidVariantPriceNames?.trim() ||
        this.translate.instant('catalog.products.variantPriceMustBePositiveFallback');
      this.toast.warningTranslated('catalog.products.variantPriceMustBePositive', { names });
    }

    if (variableNeedsVariants) {
      this.toast.warningTranslated('catalog.products.bundleNeedsVariants');
    }

    if (variantAttributesIncomplete) {
      this.toast.warningTranslated('catalog.products.variantAttributesIncomplete');
    }

    if (attributeGroupNameConflict) {
      const conflict = options.primaryAttributeGroupNameConflict;
      const toast = conflict
        ? buildAttributeGroupNameConflictToast(conflict)
        : {
            toastKey: 'catalog.products.attributeGroupNameDuplicateGeneric',
            toastParams: {},
          };
      this.toast.warningTranslated(toast.toastKey, toast.toastParams);
    }

    if (attributeValueConflict) {
      this.toast.warningTranslated('catalog.products.attributeValueDuplicate');
    }

    if (modifierGroupNameConflict) {
      const conflict = options.primaryModifierGroupNameConflict;
      const toast = conflict
        ? buildModifierGroupNameConflictToast(conflict)
        : {
            toastKey: 'catalog.products.modifierGroupNameDuplicateGeneric',
            toastParams: {},
          };
      this.toast.warningTranslated(toast.toastKey, toast.toastParams);
    }

    if (variantCombinationConflict) {
      this.toast.warningTranslated('catalog.products.variantCombinationDuplicateToast');
    }

    const anySpecificFeedback =
      variantPricesInvalid ||
      variableNeedsVariants ||
      variantAttributesIncomplete ||
      attributeGroupNameConflict ||
      attributeValueConflict ||
      modifierGroupNameConflict ||
      variantCombinationConflict;

    if (unmapped.length || (!anySpecificFeedback && errors.length > 0)) {
      this.toast.mutationFeedback(unmapped, [...errors]);
    }

    if (!hasAuthorizationError(errors) && !anySpecificFeedback) {
      options.form.markAllAsTouched();
    }
    return { handled: true, lockConflict: false };
  }

  promptReloadAfterLockConflict(
    destroyRef: DestroyRef,
    productId: string | null,
    onReload: (productId: string) => void
  ): void {
    this.confirmAction
      .confirm({
        title: this.translate.instant('catalog.products.lockVersionConflict.title'),
        message: this.translate.instant('catalog.products.lockVersionConflict.message'),
        confirmLabel: this.translate.instant('catalog.products.lockVersionConflict.reload'),
        cancelLabel: this.translate.instant('catalog.products.cancel'),
        confirmColor: 'primary',
        width: '440px',
      })
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((confirmed) => {
        if (confirmed && productId) {
          onReload(productId);
        }
      });
  }

  buildPostCreateUploadPatches(
    uploads?: ProductFormPostCreateUploadResult
  ): PostCreateUploadPatches | null {
    if (!uploads) {
      return null;
    }

    const patches: PostCreateUploadPatches = {
      pendingProductImageFile: null,
      isUploadingImage: false,
      imagePreviewUrl: null,
      imageUrl: '',
      pendingGalleryFilesCleared: false,
      isUploadingGallery: false,
      galleryItemsToAppend: [],
    };

    if (uploads.mainImage) {
      patches.imagePreviewUrl = mediaUrlForContext(
        uploads.mainImage.urls,
        'form-preview',
        uploads.mainImage.deliveryUrl
      );
      patches.imageUrl = uploads.mainImage.deliveryUrl;
    }

    if (uploads.galleryItems.length) {
      patches.pendingGalleryFilesCleared = true;
      patches.galleryItemsToAppend = [...uploads.galleryItems];
    }

    return patches;
  }

  applyPostCreateUploadPatches(
    form: { controls: { imageUrl: { setValue(value: string): void } } },
    patches: PostCreateUploadPatches,
    callbacks: {
      setPendingProductImageFile: (file: File | null) => void;
      setUploadingImage: (uploading: boolean) => void;
      setImagePreviewUrl: (url: string | null) => void;
      clearPendingGalleryFiles: () => void;
      setUploadingGallery: (uploading: boolean) => void;
      appendGalleryItems: (items: ProductGalleryImage[]) => void;
    }
  ): void {
    if (patches.pendingProductImageFile === null) {
      callbacks.setPendingProductImageFile(null);
    }
    callbacks.setUploadingImage(false);

    if (patches.imagePreviewUrl !== null) {
      callbacks.setImagePreviewUrl(patches.imagePreviewUrl);
      form.controls.imageUrl.setValue(patches.imageUrl);
    }

    if (patches.pendingGalleryFilesCleared) {
      callbacks.clearPendingGalleryFiles();
      callbacks.setUploadingGallery(false);
    }

    if (patches.galleryItemsToAppend.length) {
      callbacks.appendGalleryItems(patches.galleryItemsToAppend);
    }
  }
}
